import { useState } from 'react'
import { LuksoProfileAvatar } from './LuksoProfileAvatar'
import type { ProfileData } from '../hooks/useWallet'

interface ConnectionSectionProps {
  isConnected: boolean
  isConnecting: boolean
  address: string | null
  profileData: ProfileData | null
  isExtensionAvailable: boolean
  isWalletConnectAvailable: boolean
  error: string | null
  onConnectExtension: () => void
  onConnectWalletConnect: () => void
  onDisconnect: () => void
}

export function ConnectionSection({
  isConnected,
  isConnecting,
  address,
  profileData,
  isExtensionAvailable,
  isWalletConnectAvailable,
  error,
  onConnectExtension,
  onConnectWalletConnect,
  onDisconnect,
}: ConnectionSectionProps) {
  const [copied, setCopied] = useState(false)

  const handleCopyAddress = async () => {
    await navigator.clipboard.writeText(address || '')
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Connected state — show profile info (on canvas, no card)
  if (isConnected && address) {
    return (
      <div className="py-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            {/* Profile Avatar */}
            <LuksoProfileAvatar
              address={address}
              profileUrl={profileData?.profileImage}
              name={profileData?.profileName}
              size="xl"
              showIdenticon={true}
            />

            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-2xl">
                  {profileData?.profileName || 'Universal Profile'}
                </h3>
              </div>
              <div className="mt-1 inline-flex items-center gap-2 max-w-full">
                <p className="address font-mono text-xs sm:text-sm break-all">{address}</p>
                <button
                  type="button"
                  onClick={handleCopyAddress}
                  className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shrink-0"
                  title="Copy address"
                  aria-label="Copy Universal Profile address"
                >
                  {copied ? (
                    <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2M9 7h9a2 2 0 012 2v9a2 2 0 01-2 2h-9a2 2 0 01-2-2V9a2 2 0 012-2z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          <button
            onClick={onDisconnect}
            className="btn-secondary text-sm w-full sm:w-auto shrink-0"
          >
            Disconnect
          </button>
        </div>
      </div>
    )
  }

  // Not connected — show connection options
  const hasAnyOption = isExtensionAvailable || isWalletConnectAvailable

  if (!hasAnyOption) {
    return (
      <div className="py-16">
        <div className="text-center">
          <h3 className="text-2xl lg:text-3xl font-semibold mb-2">Permission Control</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-8">
            Connect your Universal Profile to manage controller permissions
          </p>
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            No wallet detected. Please install the LUKSO UP Browser Extension to connect your Universal Profile.
          </p>
          <a
            href="https://docs.lukso.tech/install-up-browser-extension"
            target="_blank"
            rel="noopener noreferrer"
            className="btn inline-flex items-center justify-center gap-2 max-w-xs mx-auto w-full bg-[#a8d0e8] hover:bg-[#94c4e0] text-gray-900 focus:ring-blue-400 focus:ring-offset-[#0d0d0d]"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Install Extension
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="py-16">
      <div className="text-center">
        <h1 className="text-3xl lg:text-4xl font-bold mb-2 text-white">Hub</h1>
        <p className="text-gray-400 mb-8 max-w-md mx-auto">
          Manage your Universal Profile's permissions
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-900/30 text-red-400 rounded-lg text-sm max-w-md mx-auto">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-3 max-w-sm mx-auto">
          {/* UP Browser Extension */}
          {isExtensionAvailable && (
            <button
              onClick={onConnectExtension}
              disabled={isConnecting}
              className="btn-primary inline-flex items-center justify-center gap-2 w-full"
            >
              {isConnecting ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Connecting...
                </>
              ) : (
                <>Connect Universal Profile</>
              )}
            </button>
          )}

          {/* WalletConnect */}
          {isWalletConnectAvailable && (
            <button
              onClick={onConnectWalletConnect}
              disabled={isConnecting}
              className="btn-secondary inline-flex items-center justify-center gap-2 w-full"
            >
              WalletConnect
            </button>
          )}

          {/* Install extension link when not available */}
          {!isExtensionAvailable && (
            <a
              href="https://docs.lukso.tech/install-up-browser-extension"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-gray-400 hover:text-gray-300 transition-colors"
            >
              Don't have the UP Extension? Install it here
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

